import { useCallback } from 'react';

/**
 * Hook for phone number validation and formatting
 * Provides functions for validating, cleaning, and formatting phone numbers
 */
const usePhoneValidation = (required = false) => {
  /**
   * Validates a phone number based on basic requirements
   * @param {string} value - The phone number to validate
   * @param {Object} country - The selected country object (optional)
   * @returns {string|null} - Error message or null if valid
   */
  const validatePhone = useCallback((value, country = null) => {
    // Clean the value for validation
    const cleanedValue = cleanPhoneNumber(value);
    
    // Check if field is required and empty
    if (required && (!cleanedValue || cleanedValue.length === 0)) {
      return 'Le numéro de téléphone est requis';
    }
    
    // If not required and empty, it's valid
    if (!required && (!cleanedValue || cleanedValue.length === 0)) {
      return null;
    }
    
    // Basic length validation - phone numbers should have at least 3 digits
    if (cleanedValue.length < 3) {
      return 'Le numéro de téléphone doit contenir au moins 3 chiffres';
    }
    
    // Basic format validation - should only contain digits after cleaning
    if (!/^\d+$/.test(cleanedValue)) {
      return 'Le numéro de téléphone ne doit contenir que des chiffres';
    }
    
    return null; // Valid
  }, [required]);

  /**
   * Removes non-numeric characters and spaces from phone number
   * Keeps only digits for validation and storage
   * @param {string} value - The phone number to clean
   * @returns {string} - Cleaned phone number with only digits
   */
  const cleanPhoneNumber = useCallback((value) => {
    if (!value || typeof value !== 'string') {
      return '';
    }
    
    // Remove all non-digit characters (spaces, dashes, parentheses, etc.)
    return value.replace(/\D/g, '');
  }, []);

  /**
   * Formats phone number for consistent display
   * Adds spaces for better readability while keeping it simple
   * @param {string} value - The phone number to format
   * @param {Object} country - The selected country object (optional)
   * @returns {string} - Formatted phone number
   */
  const formatPhoneNumber = useCallback((value, country = null) => {
    if (!value || typeof value !== 'string') {
      return '';
    }
    
    // Clean the number first
    const cleaned = cleanPhoneNumber(value);
    
    if (cleaned.length === 0) {
      return '';
    }
    
    // Simple formatting: add space every 2 digits for better readability
    // This is a generic approach that works for most international numbers
    let formatted = '';
    for (let i = 0; i < cleaned.length; i++) {
      if (i > 0 && i % 2 === 0) {
        formatted += ' ';
      }
      formatted += cleaned[i];
    }
    
    return formatted;
  }, [cleanPhoneNumber]);

  /**
   * Gets appropriate error message for validation failures
   * Handles different types of validation errors
   * @param {string} value - The phone number value
   * @param {Object} country - The selected country object (optional)
   * @returns {string|null} - Error message or null if valid
   */
  const getValidationError = useCallback((value, country = null) => {
    return validatePhone(value, country);
  }, [validatePhone]);

  /**
   * Checks if a phone number is valid
   * @param {string} value - The phone number to check
   * @param {Object} country - The selected country object (optional)
   * @returns {boolean} - True if valid, false otherwise
   */
  const isValidPhone = useCallback((value, country = null) => {
    return validatePhone(value, country) === null;
  }, [validatePhone]);

  return {
    validatePhone,
    cleanPhoneNumber,
    formatPhoneNumber,
    getValidationError,
    isValidPhone
  };
};

export default usePhoneValidation;